import { ButtonVariant, buttonClassName } from '@/components/form/buttonHelper';
import { FileInput } from '@/components/form/fields/file/FileInput';
import { Step } from '@/components/multistep/Questionnaire';
import { useControls } from '@/components/multistep/useMultistep';
import SiDownload from '@/icons/SiDownload';
import { FinanceModelTypes } from '@/services/api/contracts/financemodels';
import { useRegions } from '@/services/api/hooks/region';
import { useCSV } from '@/services/csv/useCSVService';
import { useCallback, useMemo } from 'react';
import { useBulkFinanceSmartNext } from '../helpers/useBulkFinanceSmartNext';
import {
  BulkFinanceModelTypes,
  constructCSVModelIdentifier,
  getDuplicateEntries,
  isPeripheralModel
} from '../helpers/utils';

export type FinanceModelCSVParser = typeof import('../CSVParsingWorker');
export type Result = Awaited<ReturnType<FinanceModelCSVParser['processFinanceModelCSV']>>;

const createTemplate = (regions: string[]) => {
  const headings = ['Category', 'Manufacturer', 'Type', 'Term'];
  regions.forEach(region => {
    headings.push(`PCM:${region}`);
  });

  const examples = [
    ['Laptop', 'Dell', 'FMV', '3'],
    ['Peripheral', '', 'FPL', '1']
  ];
  examples.map(example => {
    regions.forEach(_region => {
      example.push('0.6');
    });
  });

  const head = headings.join(',');
  return examples.reduce((template, row) => {
    return template + '\n' + row.join(',');
  }, head);
};

export const UploadFinanceModelStep = () => {
  const [onComplete, canMoveOn] = useCSV(state => [state.onComplete, !!state.result]);
  const { skipToStep } = useControls();
  const next = useBulkFinanceSmartNext();
  const [regions] = useRegions();

  const template = useMemo(() => {
    const template = createTemplate(regions.map(region => region.code));
    return URL.createObjectURL(new Blob([template], { type: 'octet/stream' }));
  }, []);

  const processAndProceed = async (file: File) => {
    // Create an instance of a worker using comlink syntactic sugar
    const instance = new ComlinkWorker<FinanceModelCSVParser>(new URL('../CSVParsingWorker', import.meta.url));

    const result = await instance.processFinanceModelCSV(file);

    if (result.data.length) {
      result.errors.genericErrors = [];

      const duplicateEntryRows = getDuplicateEntries(result.data);

      const invalidPeripheralTypeRows = result.data.filter(item => {
        const type = item.Type.toUpperCase();

        if (isPeripheralModel(item) && type === BulkFinanceModelTypes.FMV) return true;
      });

      const missingNonPeripheralManufacturerRows = result.data.filter(item => {
        return !isPeripheralModel(item) && !item.Manufacturer;
      });

      const invalidTypeRows = result.data.filter(item => {
        return !Object.values(BulkFinanceModelTypes).includes(item.Type);
      });

      const invalidTermRows = result.data
        .map(item => {
          if (isPeripheralModel(item) && item.Type === BulkFinanceModelTypes.FMV) return;
          if (!item.Term) return `Missing term value for ${constructCSVModelIdentifier(item)}`;
        })
        .filter(row => row) as string[];

      const invalidPCMRows = result.data
        .map(item => {
          const hasPcm = item.Pcms.some(pcm => pcm.value);

          if (isPeripheralModel(item) && item.Type === BulkFinanceModelTypes.FMV) return;
          if (!hasPcm) return `Missing PCM value for ${constructCSVModelIdentifier(item)}`;
        })
        .filter(row => row) as string[];

      result.errors.genericErrors.push(
        ...invalidTypeRows.map(item => {
          return {
            message: `Invalid finance model type: ${item.Type}. Please ensure finance model types are either ${FinanceModelTypes.FPL} or ${FinanceModelTypes.FMV}`
          };
        }),
        ...invalidPeripheralTypeRows.map(item => {
          return {
            message: `Invalid peripheral finance model type: ${item.Type}. Peripheral finance model types must be ${FinanceModelTypes.FPL}`
          };
        }),
        ...duplicateEntryRows.map(item => {
          return {
            message: `Duplicate finance model: ${item}. Please ensure finance models are unique`
          };
        }),
        ...missingNonPeripheralManufacturerRows.map(item => {
          return {
            message: `Missing manufacturer for finance model: ${constructCSVModelIdentifier(
              item
            )}. Please ensure non peripheral finance models have a manufacturer`
          };
        }),
        ...invalidTermRows.map(message => {
          return {
            message
          };
        }),
        ...invalidPCMRows.map(message => {
          return {
            message
          };
        })
      );
    }

    onComplete(result);

    // If there any issues, show them
    if (
      result.errors.couldNotParse.length ||
      result.errors.couldNotValidate.length ||
      result.errors.genericErrors?.length
    ) {
      skipToStep('issues');
      return;
    }

    await next(result);
  };

  const validate = useCallback(() => canMoveOn, [canMoveOn]);
  return (
    <Step
      validate={validate}
      controls="manual"
      id="upload"
      title="Upload"
      description="Upload a spreadsheet of finance models to bulk upload them to the system."
    >
      <div className="flex w-full flex-col gap-2">
        <a
          className={buttonClassName(ButtonVariant.Mono)}
          href={template}
          download="bulk-upload-finance-models-template.csv"
        >
          <SiDownload />
          <span>Download template</span>
        </a>
        <FileInput
          id="csv"
          placeholder="Drop or click to upload spreadsheet"
          value={[]}
          onChange={([file]) => processAndProceed(file)}
        />
      </div>
    </Step>
  );
};
